import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { sendQuery, sendFeedback } from "../services/api";
import type { Conversation } from "../types/chat";
import { chatboxData } from "../data/chatData";

// ─── State Shape ─────────────────────────────────────────────────────────────

interface ChatState {
  showWelcome: boolean;
  isTyping: boolean;
  showBg: boolean;
  isRecord: boolean;
  showDrawer: boolean;
  showFeedback: boolean;
  showFeedbackSuccess: boolean;
  feedbackAnswerId: string | null;
  conversations: Conversation[];
}

const initialState: ChatState = {
  showWelcome: true,
  isTyping: false,
  showBg: false,
  isRecord: false,
  showDrawer: false,
  showFeedback: false,
  showFeedbackSuccess: false,
  feedbackAnswerId: null,
  conversations: [],
};

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const submitUserQuery = createAsyncThunk(
  "chat/submitUserQuery",
  async (payload: { query: string; inputType: string; file?: File | null }) => {
    const { query, inputType, file } = payload;
    const response = await sendQuery(query, inputType, file);
    return { response, inputType };
  }
);

export const submitFeedback = createAsyncThunk(
  "chat/submitFeedback",
  async (payload: { id: string; text: string; like: "true" | "false" }) => {
    const { id, text, like } = payload;
    await sendFeedback(id, text, like);
    return payload;
  }
);



const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    startBot(state) {
      state.showWelcome = false;
      state.conversations = [
        {
          query: chatboxData.initialText.en.text,
          time: new Date().toISOString(),
          type: "bot",
          audio: chatboxData.initialText.en.audio,
          isplaying: false,
        },
      ];
    },

    pushLocalUserMessage(
      state,
      action: PayloadAction<{ query: string; inputType: string; fileName?: string | null }>
    ) {
      // Stop any playing audio
      state.conversations.forEach((c) => {
        c.isplaying = false;
      });

      // Push the user's message
      state.conversations.push({
        query: action.payload.query,
        time: new Date().toISOString(),
        type: "user",
        fileName: action.payload.fileName ?? null,
      });

      // Deactivate the previous bot message
      const len = state.conversations.length;
      if (len > 2) {
        state.conversations[len - 2].active = false;
      }

      // Add typing indicator
      state.isTyping = true;
      state.conversations.push({
        query: "",
        time: new Date().toISOString(),
        type: "bot",
        typing: true,
      });
    },

    setRecord(state, action: PayloadAction<boolean>) {
      state.isRecord = action.payload;
    },

    setBg(state, action: PayloadAction<boolean>) {
      state.showBg = action.payload;
    },

    setDrawer(state, action: PayloadAction<boolean>) {
      state.showDrawer = action.payload;
    },

    startFeedback(state, action: PayloadAction<{ id: string; like: boolean }>) {
      state.feedbackAnswerId = action.payload.id;
      if (!action.payload.like) {
        state.showFeedback = true;
        state.showBg = true;
      }
    },

    cancelFeedback(state) {
      state.showFeedback = false;
      state.feedbackAnswerId = null;
      state.showBg = false;
    },

    hideFeedbackSuccess(state) {
      state.showFeedbackSuccess = false;
      state.showFeedback = false;
      state.showBg = false;
    },

    setCourseIndex(
      state,
      action: PayloadAction<{ convoIndex: number; activeIndex: number }>
    ) {
      const { convoIndex, activeIndex } = action.payload;
      if (state.conversations[convoIndex]) {
        state.conversations[convoIndex].activeIndex = activeIndex;
      }
    },

    togglePlaying(state, action: PayloadAction<number>) {
      const index = action.payload;
      if (state.conversations[index]) {
        state.conversations[index].isplaying =
          !state.conversations[index].isplaying;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(submitUserQuery.fulfilled, (state, action) => {
        // Remove typing placeholder
        state.conversations = state.conversations.filter((c) => !c.typing);
        state.isTyping = false;

        const { response } = action.payload;
        const answerText: string = response.answer ?? "";

        const urlRegex = /https?:\/\/[^\s)]+/g;
        const urls = answerText.match(urlRegex) ?? [];
        const brochureLink = urls.length > 0 ? urls[0] : null;

        state.conversations.push({
          query: answerText,
          time: new Date().toISOString(),
          type: "bot",
          audio: response.audio,
          isplaying: false,
          ask_for_resume: response.ask_for_resume ?? false,
          show_download: urls.length === 0 ? response.show_download ?? false : false,
          courses: response.suggested_courses ?? [],
          activeIndex: 0,
          answerId: response.answerId,
          feedbacksend: false,
          isFeedback: !!response.getFeedback,
          brochureLink,
        });
      })

      .addCase(submitUserQuery.rejected, (state, action) => {
        state.conversations = state.conversations.filter((c) => !c.typing);
        state.isTyping = false;
        
        const inputType = action.meta.arg.inputType;
        let msg = "";
        let audio = undefined;

        if (navigator.onLine) {
          msg = inputType === "VOICE" 
            ? chatboxData.fallback.error_voice.en.answer 
            : chatboxData.fallback.error_text.en.answer;
          audio = inputType === "VOICE"
            ? chatboxData.fallback.error_voice.en.audio
            : chatboxData.fallback.error_text.en.audio;
        } else {
          msg = inputType === "VOICE"
            ? chatboxData.fallback.offline_voice.en.answer
            : chatboxData.fallback.offline_text.en.answer;
          audio = inputType === "VOICE"
            ? chatboxData.fallback.offline_voice.en.audio
            : chatboxData.fallback.offline_text.en.audio;
        }

        state.conversations.push({
          query: msg,
          time: new Date().toISOString(),
          type: "bot",
          audio,
        });
      })

      .addCase(submitFeedback.fulfilled, (state, action) => {
        state.showFeedback = false;
        state.showFeedbackSuccess = true;
        state.showBg = true;
        const conv = state.conversations.find(
          (c) => c.answerId === action.payload.id
        );
        if (conv) {
          conv.feedbacksend = true;
        }
      });
  },
});

export const {
  startBot,
  pushLocalUserMessage,
  setRecord,
  setBg,
  setDrawer,
  startFeedback,
  cancelFeedback,
  hideFeedbackSuccess,
  setCourseIndex,
  togglePlaying,
} = chatSlice.actions;

export default chatSlice.reducer;
